import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeviceCameraIcon,
  IterationsIcon,
  TrashIcon,
} from '@primer/octicons-react';
import { GraphQLError } from 'graphql';
import React, { Suspense, useRef, useState } from 'react';
import { graphql, useFragment } from 'react-relay';

import useShortcutKey from '../../../hooks/useShortcutKey';
import useToast from '../../../hooks/useToast';
import { BookBookScrapCreateDialog_book$key } from '../../../relay/__generated__/BookBookScrapCreateDialog_book.graphql';
import { BookBookScrapCreateDialog_bookScrapCreateMutation } from '../../../relay/__generated__/BookBookScrapCreateDialog_bookScrapCreateMutation.graphql';
import { parseGraphQLError } from '../../../utils/error';
import { uploadFile } from '../../../utils/file';
import { isNullable } from '../../../utils/is';
import { scrollToBottom } from '../../../utils/scroll';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import ErrorBoundary from '../../core/ErrorBoundary';
import Grid from '../../core/Grid';
import IconButton from '../../core/IconButton';
import CoreImage from '../../core/Image';
import ItemList from '../../core/ItemList';
import Kbd from '../../core/Kbd';
import Label from '../../core/Label';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NumberField, { NumberFieldProps } from '../../core/NumberField';
import PdfCropper, { Cropper, PdfCropperProps } from '../../core/PdfCropper';
import SquareView from '../../core/SquareView';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import TextField from '../../core/TextField';
import View from '../../core/View';

const BookBookScrapCreateDialog_book = graphql`
  fragment BookBookScrapCreateDialog_book on Book {
    id
    title
    solutionPdf {
      id
      objectUrl
    }
    problemPdf {
      id
      objectUrl
    }
    latestBookScraps: bookScraps(order: { created: DESC }, first: 1) {
      totalCount
      edges {
        node {
          id
          problemPage
          solutionPage
          problemBookPage
          solutionBookPage
          problemNumber
        }
      }
    }
  }
`;

type CroppedImageType = {
  width: number;
  height: number;
  objectUrl: string;
};

type FormikStatusType = {
  problems: CroppedImageType[];
  solutions: CroppedImageType[];
};

type Props = {
  book: BookBookScrapCreateDialog_book$key;
} & DialogProps &
  Pick<MutationFormikProps<BookBookScrapCreateDialog_bookScrapCreateMutation>, 'onSubmit' | 'config' | 'connections'>;

const BookBookScrapCreateDialog = ({ book, onSubmit, config, connections, ...props }: Props) => {
  const { id, title, solutionPdf, problemPdf, latestBookScraps } = useFragment(BookBookScrapCreateDialog_book, book);

  const { toast } = useToast();

  const problemPdfCropperRef = useRef<Cropper>(null);
  const solutionPdfCropperRef = useRef<Cropper>(null);

  const [problemTotalPage, setProblemTotalPage] = useState(1);
  const [solutionTotalPage, setSolutionTotalPage] = useState(1);

  const handleLoadSuccessProblemPdf: PdfCropperProps['onLoadSuccess'] = (pdf) => {
    setProblemTotalPage(pdf.numPages);
  };
  const handleLoadSuccessSolutionPdf: PdfCropperProps['onLoadSuccess'] = (pdf) => {
    setSolutionTotalPage(pdf.numPages);
  };

  const problemPreviewContainerRef = useRef<HTMLDivElement>(null);
  const solutionPreviewContainerRef = useRef<HTMLDivElement>(null);

  const cropBothButtonRef = useRef<HTMLButtonElement>(null);
  const cropProblemButtonRef = useRef<HTMLButtonElement>(null);
  const cropSolutionButtonRef = useRef<HTMLButtonElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  useShortcutKey(
    (e) => ((e.target as HTMLElement).tagName === 'INPUT' ? false : e.ctrlKey && e.code === 'KeyC'),
    () => cropBothButtonRef.current?.click(),
  );
  useShortcutKey(
    (e) => ((e.target as HTMLElement).tagName === 'INPUT' ? false : e.ctrlKey && e.code === 'KeyV'),
    () => cropSolutionButtonRef.current?.click(),
  );
  useShortcutKey(
    (e) => ((e.target as HTMLElement).tagName === 'INPUT' ? false : e.ctrlKey && e.code === 'KeyX'),
    () => cropProblemButtonRef.current?.click(),
  );
  useShortcutKey(
    (e) => e.ctrlKey && e.code === 'KeyS',
    () => saveButtonRef.current?.click(),
  );

  /**
   * 다음 문제 번호를 예측하는 함수입니다.
   * 시중 교재의 문제 번호는 string이기 때문에 다음 번호를 (현재 번호 + 1)과 같은 방식으로 예측할 수가 없습니다.
   *
   * 그래서 다음과 같은 방식으로 다음 번호를 예측합니다.
   * 1. problemNumber에서 가장 마지막에 있는 숫자를 찾습니다.
   * 2. 그 숫자에 1을 더한 값으로 다음 번호를 예측합니다.
   */
  const guessNextProblemNumber = (problemNumber: string) => {
    const regexForLastNumberInString = /\d+(?!.*\d)/;

    const lastNumberInProblemNumber = problemNumber.match(regexForLastNumberInString);
    if (lastNumberInProblemNumber) {
      const lastNumber = Number(lastNumberInProblemNumber[0]);
      return problemNumber.replace(regexForLastNumberInString, String(lastNumber + 1));
    }

    return problemNumber;
  };

  const initialValue = {
    book: id,
    problemPage: 1,
    solutionPage: 1,
    problemBookPage: 1,
    solutionBookPage: 1,
    problemNumber: '',
    problem: { key: '', size: 0 },
    solution: { key: '', size: 0 },
    ...(latestBookScraps.totalCount && latestBookScraps.totalCount > 0 && latestBookScraps.edges[0].node
      ? {
          problemPage: latestBookScraps.edges[0].node.problemPage,
          solutionPage: latestBookScraps.edges[0].node.solutionPage ?? 1,
          problemBookPage: latestBookScraps.edges[0].node.problemBookPage,
          solutionBookPage: latestBookScraps.edges[0].node.solutionBookPage ?? 1,
          problemNumber: guessNextProblemNumber(latestBookScraps.edges[0].node.problemNumber),
        }
      : {}),
  };

  const mergeCroppedImages = async (images: CroppedImageType[]) => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(...images.map(({ width }) => width));
    canvas.height = images.reduce((acc, cur) => acc + cur.height, 0);
    const ctx = canvas.getContext('2d');

    images.forEach(({ width, height, objectUrl }, i, images) => {
      const imageElement = new Image();
      imageElement.src = objectUrl;
      imageElement.width = width;
      imageElement.height = height;
      ctx?.drawImage(
        imageElement,
        0,
        images.slice(0, i).reduce((acc, cur) => acc + cur.height, 0),
      );
    });

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve));
    canvas.remove();

    if (isNullable(blob)) return Promise.reject('empty_merged_image');

    return blob;
  };

  return (
    <Dialog full {...props}>
      <Suspense>
        <MutationFormik<BookBookScrapCreateDialog_bookScrapCreateMutation>
          mutation={graphql`
            mutation BookBookScrapCreateDialog_bookScrapCreateMutation($input: BookScrapCreateInput!) {
              bookScrapCreate(input: $input) {
                id
                problemPage
                solutionPage
                problemBookPage
                solutionBookPage
                problemNumber
                book {
                  id
                  ...BookBookScrapPaginator_book
                  unclassifiedBookScrapCount
                  ...BookActionsMutationButtonStack_book
                }
              }
            }
          `}
          initialValues={{ ...initialValue }}
          onSubmit={onSubmit}
          config={{
            ...config,
            onCompleted: (response, _error, formikHelper) => {
              config?.onCompleted?.(response, _error, formikHelper);
              const { bookScrapCreate } = response;
              formikHelper.resetForm({
                values: {
                  ...initialValue,
                  problemPage: bookScrapCreate.problemPage,
                  solutionPage: bookScrapCreate.solutionPage ?? 1,
                  problemBookPage: bookScrapCreate.problemBookPage,
                  solutionBookPage: bookScrapCreate.solutionBookPage ?? 1,
                  problemNumber: guessNextProblemNumber(bookScrapCreate.problemNumber),
                },
              });
            },
            onError: (...args) => {
              config?.onError?.(...args);
              toast(parseGraphQLError(args[0] as GraphQLError)?.[0].message || '오류가 발생했어요', 'error');
            },
          }}
          initialStatus={{ problems: [], solutions: [] } as FormikStatusType}
          connections={connections}
          enableReinitialize={false}
        >
          {({ values, setFieldValue, status: _status, setStatus, setSubmitting, submitForm }) => {
            const status = _status as FormikStatusType;
            const handleChangeProblemPdfPage: NumberFieldProps['onChange'] = (e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              const isValid = value === '' || (value >= 1 && value <= problemTotalPage);
              if (isValid) {
                setFieldValue('problemPage', value);
                setFieldValue('problemBookPage', value);
              }
            };
            const handleChangeSolutionPdfPage: NumberFieldProps['onChange'] = (e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              const isValid = value === '' || (value >= 1 && value <= solutionTotalPage);
              if (isValid) {
                setFieldValue('solutionPage', value);
                setFieldValue('solutionBookPage', value);
              }
            };

            const handleClickProblemPdfPageIconButton = (offset: number) => {
              const value = values.problemPage + offset;
              const isValid = value >= 1 && value <= problemTotalPage;
              if (isValid) {
                setFieldValue('problemPage', value);
                setFieldValue('problemBookPage', value);
              }
            };
            const handleClickSolutionPdfPageIconButton = (offset: number) => {
              const value = values.solutionPage + offset;
              const isValid = value >= 1 && value <= solutionTotalPage;
              if (isValid) {
                setFieldValue('solutionPage', value);
                setFieldValue('solutionBookPage', value);
              }
            };

            const getCroppedImageObjectFromCropper = (cropper: Cropper): CroppedImageType => ({
              width: cropper.getCroppedCanvas().width,
              height: cropper.getCroppedCanvas().height,
              objectUrl: cropper.getCroppedCanvas().toDataURL(),
            });

            const handleClickProblemTrashButton = (index: number) => {
              setStatus({ ...status, problems: status.problems.filter((_, i) => i !== index) });
            };
            const handleClickSolutionTrashButton = (index: number) => {
              setStatus({ ...status, solutions: status.solutions.filter((_, i) => i !== index) });
            };
            const availableSubmit = status.solutions.length > 0 && status.problems.length > 0;

            return (
              <>
                <Dialog.Header>
                  <Grid gapX={3} sx={{ paddingRight: 4, alignItems: 'center' }}>
                    <Grid.Unit size={'max'}>
                      <Stack gapX={1}>
                        <Stack.Item>
                          <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>스크랩하기</Text>
                        </Stack.Item>
                        <Stack.Item>
                          <View>
                            <Label variant={'accent'}>{title}</Label>
                          </View>
                        </Stack.Item>
                      </Stack>
                    </Grid.Unit>
                    <Grid.Unit size={'min'}>
                      <Text fontSize={0} whiteSpace={'pre-wrap'}>
                        <Kbd>Ctrl</Kbd> + <Kbd>S</Kbd>: 저장 및 다음 / <Kbd>Ctrl</Kbd> + <Kbd>X</Kbd>: 문제 스크랩 /{' '}
                        <Kbd>Ctrl</Kbd> + <Kbd>C</Kbd>: 함께 스크랩 / <Kbd>Ctrl</Kbd> + <Kbd>V</Kbd>: 해설 스크랩
                      </Text>
                    </Grid.Unit>
                    <Grid.Unit size={'min'}>
                      <Button
                        ref={saveButtonRef}
                        size={'large'}
                        variant={'primary'}
                        onClick={async () => {
                          setSubmitting(true);

                          try {
                            await setFieldValueAfterMergingAndUploadingImage('problem', status.problems);
                            await setFieldValueAfterMergingAndUploadingImage('solution', status.solutions);

                            setTimeout(() => submitForm());
                          } catch (e) {
                            if (e === 'empty_merged_image') {
                              toast('이미지 병합이 제대로 이루어지지 않았습니다', 'error');
                            } else if (e === 'block_multipart_upload')
                              toast('사진 크기가 너무 큽니다. 더 작게 캡쳐해주세요.', 'error');
                            else {
                              toast('다시 시도해 주세요;m&u', 'error');
                            }
                          }

                          setSubmitting(false);

                          async function setFieldValueAfterMergingAndUploadingImage(
                            fieldName: string,
                            images: CroppedImageType[],
                          ) {
                            const blob = await mergeCroppedImages(images);
                            const [, extension] = blob.type.split('/');
                            const value = await uploadFile(blob, {
                              multipart: false,
                              fieldName: fieldName,
                              formatFileName: () => `${id}-${values.problemNumber}-${fieldName}.${extension}`,
                            });
                            setFieldValue(fieldName, value);
                          }
                        }}
                        leadingIcon={CheckIcon}
                        disabled={!availableSubmit}
                      >
                        저장 및 다음
                      </Button>
                    </Grid.Unit>
                  </Grid>
                </Dialog.Header>
                <Grid wrap={false} sx={{ height: '100%', overflowY: 'hidden' }}>
                  <Grid.Unit
                    size={[3 / 4, 3 / 4, 3 / 4, 3 / 4, 4 / 5]}
                    sx={{ padding: 3, height: '100%', overflowY: 'auto' }}
                  >
                    <Grid gapX={4}>
                      <Grid.Unit size={1 / 2}>
                        <Grid sx={{ marginBottom: 3, alignItems: 'center' }}>
                          <Grid.Unit size={'max'}>
                            <Stack gapX={3}>
                              <Stack.Item>
                                <Text fontSize={2} fontWeight={'bold'}>
                                  문제
                                </Text>
                              </Stack.Item>
                              <Stack.Item>
                                <Stack gapX={1}>
                                  <Stack.Item>
                                    <IconButton
                                      icon={ChevronLeftIcon}
                                      aria-label={'previous problem pdf page'}
                                      size={'large'}
                                      disabled={values.problemPage <= 1}
                                      onClick={() => handleClickProblemPdfPageIconButton(-1)}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <NumberField
                                      sx={{ width: 60 }}
                                      label={'문제 페이지'}
                                      labelConfig={{ visuallyHidden: true }}
                                      name={'problemPage'}
                                      min={1}
                                      max={problemTotalPage}
                                      onChange={handleChangeProblemPdfPage}
                                      size={'large'}
                                      debounce
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <IconButton
                                      icon={ChevronRightIcon}
                                      aria-label={'next problem pdf page'}
                                      size={'large'}
                                      disabled={values.problemPage >= problemTotalPage}
                                      onClick={() => handleClickProblemPdfPageIconButton(1)}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <Text fontSize={2}>{`/ ${problemTotalPage || 1}P`}</Text>
                                  </Stack.Item>
                                </Stack>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                          <Grid.Unit size={'min'}>
                            <Stack gapX={2}>
                              <Stack.Item>
                                <IconButton
                                  icon={IterationsIcon}
                                  aria-label={'rotate problem pdf'}
                                  size={'large'}
                                  onClick={() => {
                                    if (!isNullable(problemPdfCropperRef.current))
                                      problemPdfCropperRef.current.rotate(270);
                                  }}
                                />
                              </Stack.Item>
                              <Stack.Item>
                                <IconButton
                                  ref={cropProblemButtonRef}
                                  icon={DeviceCameraIcon}
                                  aria-label={'crop problem pdf'}
                                  size={'large'}
                                  onClick={() => {
                                    if (!isNullable(problemPdfCropperRef.current)) {
                                      setStatus({
                                        ...status,
                                        problems: [
                                          ...status.problems,
                                          getCroppedImageObjectFromCropper(problemPdfCropperRef.current),
                                        ],
                                      });
                                      setTimeout(() => {
                                        if (problemPreviewContainerRef.current)
                                          scrollToBottom(problemPreviewContainerRef.current, {});
                                      }, 300);
                                    }
                                  }}
                                />
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                        </Grid>
                        <ErrorBoundary>
                          <PdfCropper
                            ref={problemPdfCropperRef}
                            src={problemPdf.objectUrl}
                            pageNumber={values.problemPage}
                            onLoadSuccess={handleLoadSuccessProblemPdf}
                            cropperHeight={'75vh'}
                          />
                        </ErrorBoundary>
                      </Grid.Unit>
                      <Grid.Unit size={1 / 2}>
                        <Grid sx={{ marginBottom: 3, alignItems: 'center' }}>
                          <Grid.Unit size={'max'}>
                            <Stack gapX={3}>
                              <Stack.Item>
                                <Text fontSize={2} fontWeight={'bold'}>
                                  {'해설'}
                                </Text>
                              </Stack.Item>
                              <Stack.Item>
                                <Stack gapX={1}>
                                  <Stack.Item>
                                    <IconButton
                                      icon={ChevronLeftIcon}
                                      aria-label={'previous solution pdf page'}
                                      size={'large'}
                                      disabled={values.solutionPage <= 1}
                                      onClick={() => handleClickSolutionPdfPageIconButton(-1)}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <NumberField
                                      sx={{ width: 60 }}
                                      label={'해설 페이지'}
                                      labelConfig={{ visuallyHidden: true }}
                                      name={'solutionPage'}
                                      min={1}
                                      max={solutionTotalPage}
                                      size={'large'}
                                      onChange={handleChangeSolutionPdfPage}
                                      debounce
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <IconButton
                                      icon={ChevronRightIcon}
                                      aria-label={'next solution pdf page'}
                                      size={'large'}
                                      disabled={values.solutionPage >= solutionTotalPage}
                                      onClick={() => handleClickSolutionPdfPageIconButton(1)}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <Text fontSize={2}>{`/ ${solutionTotalPage || 1}P`}</Text>
                                  </Stack.Item>
                                </Stack>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                          <Grid.Unit size={'min'}>
                            <Stack gapX={2}>
                              <Stack.Item>
                                <IconButton
                                  icon={IterationsIcon}
                                  aria-label={'rotate solution pdf'}
                                  size={'large'}
                                  onClick={() => {
                                    if (!isNullable(solutionPdfCropperRef.current))
                                      solutionPdfCropperRef.current.rotate(270);
                                  }}
                                />
                              </Stack.Item>
                              <Stack.Item>
                                <IconButton
                                  ref={cropSolutionButtonRef}
                                  icon={DeviceCameraIcon}
                                  aria-label={'crop solution pdf'}
                                  size={'large'}
                                  onClick={() => {
                                    if (!isNullable(solutionPdfCropperRef.current)) {
                                      setStatus({
                                        ...status,
                                        solutions: [
                                          ...status.solutions,
                                          getCroppedImageObjectFromCropper(solutionPdfCropperRef.current),
                                        ],
                                      });

                                      setTimeout(() => {
                                        if (solutionPreviewContainerRef.current)
                                          scrollToBottom(solutionPreviewContainerRef.current, {});
                                      }, 300);
                                    }
                                  }}
                                />
                              </Stack.Item>
                              <Stack.Item>
                                <Button
                                  ref={cropBothButtonRef}
                                  size={'large'}
                                  onClick={() => {
                                    if (
                                      !isNullable(problemPdfCropperRef.current) &&
                                      !isNullable(solutionPdfCropperRef.current)
                                    ) {
                                      setStatus({
                                        problems: [
                                          ...status.problems,
                                          getCroppedImageObjectFromCropper(problemPdfCropperRef.current),
                                        ],
                                        solutions: [
                                          ...status.solutions,
                                          getCroppedImageObjectFromCropper(solutionPdfCropperRef.current),
                                        ],
                                      });

                                      setTimeout(() => {
                                        if (problemPreviewContainerRef.current)
                                          scrollToBottom(problemPreviewContainerRef.current, {});
                                        if (solutionPreviewContainerRef.current)
                                          scrollToBottom(solutionPreviewContainerRef.current, {});
                                      }, 300);
                                    }
                                  }}
                                >
                                  함께 스크랩
                                </Button>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                        </Grid>
                        <ErrorBoundary>
                          <PdfCropper
                            ref={solutionPdfCropperRef}
                            src={solutionPdf.objectUrl}
                            pageNumber={values.solutionPage}
                            onLoadSuccess={handleLoadSuccessSolutionPdf}
                            cropperHeight={'75vh'}
                          />
                        </ErrorBoundary>
                      </Grid.Unit>
                    </Grid>
                  </Grid.Unit>
                  <Grid.Unit size={[1 / 4, 1 / 4, 1 / 4, 1 / 4, 1 / 5]}>
                    <View
                      sx={{
                        'padding': 3,
                        'backgroundColor': 'canvas.inset',
                        'height': '100%',
                        'overflowY': 'auto',
                        '&>*:not(:first-child)': { marginTop: 3 },
                      }}
                    >
                      <View>
                        <Grid sx={{ alignItems: 'center' }}>
                          <Grid.Unit size={'max'}>
                            <Stack gapX={1}>
                              <Stack.Item>
                                <Text fontSize={2} fontWeight={'bold'}>
                                  문제
                                </Text>
                              </Stack.Item>
                              <Stack.Item>
                                <Text
                                  fontSize={1}
                                  fontWeight={'bold'}
                                  color={status.problems.length === 0 ? 'neutral.emphasis' : 'accent.emphasis'}
                                >
                                  {status.problems.length}
                                </Text>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                          <Grid.Unit size={'min'}>
                            <Stack gapX={2}>
                              <Stack.Item>
                                <Stack gapX={1}>
                                  <Stack.Item>
                                    <NumberField
                                      sx={{ width: 88 }}
                                      label={'문제 책 페이지'}
                                      labelConfig={{ visuallyHidden: true }}
                                      name={'problemBookPage'}
                                      min={1}
                                      size={'large'}
                                    />
                                  </Stack.Item>
                                  <Stack.Item>
                                    <Text fontSize={1}>P</Text>
                                  </Stack.Item>
                                </Stack>
                              </Stack.Item>
                              <Stack.Item>
                                <Stack gapX={1}>
                                  <Stack.Item>
                                    <TextField
                                      sx={{ width: 88 }}
                                      label={'문제 번호'}
                                      labelConfig={{ visuallyHidden: true }}
                                      placeholder={'문제 번호'}
                                      name={'problemNumber'}
                                      size={'large'}
                                    />
                                  </Stack.Item>
                                </Stack>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                        </Grid>
                        <SquareView
                          sx={{ borderColor: 'border.default', borderStyle: 'solid', borderWidth: 1, marginTop: 2 }}
                        >
                          <View
                            ref={problemPreviewContainerRef}
                            sx={{
                              height: '100%',
                              width: '100%',
                              overflowY: 'auto',
                              backgroundColor: 'canvas.default',
                            }}
                          >
                            <ItemList
                              items={status.problems}
                              renderItem={({ objectUrl }, index) => (
                                <View
                                  sx={{
                                    'position': 'relative',
                                    '&:hover': {
                                      '*': {
                                        display: 'block',
                                      },
                                    },
                                  }}
                                >
                                  <View
                                    sx={{
                                      position: 'absolute',
                                      display: 'none',
                                      width: '100%',
                                      height: '100%',
                                      backgroundColor: 'fg.default',
                                      opacity: 0.2,
                                      zIndex: 1,
                                    }}
                                  />
                                  <IconButton
                                    sx={{ position: 'absolute', display: 'none', left: 2, bottom: 2, zIndex: 1 }}
                                    size={'large'}
                                    icon={TrashIcon}
                                    aria-label={`delete problem image${index}`}
                                    onClick={() => {
                                      handleClickProblemTrashButton(index);
                                    }}
                                  />
                                  <CoreImage
                                    src={objectUrl || ''}
                                    alt={`problem image${index}`}
                                    html
                                    style={{ display: 'block' }}
                                  />
                                </View>
                              )}
                              renderItemWrapper={(children, { objectUrl }, index) => (
                                <View key={objectUrl + index}>{children}</View>
                              )}
                              emptyState={
                                <View
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                  }}
                                >
                                  <Text fontSize={2} color={'neutral.emphasis'}>
                                    {'문제를 스크랩 해주세요'}
                                  </Text>
                                </View>
                              }
                            />
                          </View>
                        </SquareView>
                      </View>
                      <View>
                        <Grid sx={{ alignItems: 'center' }}>
                          <Grid.Unit size={'max'}>
                            <Stack gapX={1}>
                              <Stack.Item>
                                <Text fontSize={2} fontWeight={'bold'}>
                                  해설
                                </Text>
                              </Stack.Item>
                              <Stack.Item>
                                <Text
                                  fontSize={1}
                                  fontWeight={'bold'}
                                  color={status.solutions.length === 0 ? 'neutral.emphasis' : 'accent.emphasis'}
                                >
                                  {status.solutions.length}
                                </Text>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                          <Grid.Unit size={'min'}>
                            <Stack gapX={1}>
                              <Stack.Item>
                                <NumberField
                                  sx={{ width: 88 }}
                                  label={'해설 책 페이지'}
                                  labelConfig={{ visuallyHidden: true }}
                                  name={'solutionBookPage'}
                                  min={1}
                                  size={'large'}
                                />
                              </Stack.Item>
                              <Stack.Item>
                                <Text fontSize={1}>P</Text>
                              </Stack.Item>
                            </Stack>
                          </Grid.Unit>
                        </Grid>
                        <SquareView
                          sx={{ borderColor: 'border.default', borderStyle: 'solid', borderWidth: 1, marginTop: 2 }}
                        >
                          <View
                            ref={solutionPreviewContainerRef}
                            sx={{
                              height: '100%',
                              width: '100%',
                              overflowY: 'auto',
                              backgroundColor: 'canvas.default',
                            }}
                          >
                            <ItemList
                              items={status.solutions}
                              renderItem={({ objectUrl }, index) => (
                                <View
                                  sx={{
                                    'position': 'relative',
                                    '&:hover': {
                                      '*': {
                                        display: 'block',
                                      },
                                    },
                                  }}
                                >
                                  <View
                                    sx={{
                                      position: 'absolute',
                                      display: 'none',
                                      width: '100%',
                                      height: '100%',
                                      backgroundColor: 'fg.default',
                                      opacity: 0.2,
                                      zIndex: 1,
                                    }}
                                  />
                                  <IconButton
                                    sx={{ position: 'absolute', display: 'none', left: 2, bottom: 2, zIndex: 1 }}
                                    size={'large'}
                                    icon={TrashIcon}
                                    aria-label={`delete solution image${index}`}
                                    onClick={() => {
                                      handleClickSolutionTrashButton(index);
                                    }}
                                  />
                                  <CoreImage
                                    src={objectUrl || ''}
                                    alt={`solution image${index}`}
                                    html
                                    style={{ display: 'block' }}
                                  />
                                </View>
                              )}
                              renderItemWrapper={(children, { objectUrl }, index) => (
                                <View key={objectUrl + index}>{children}</View>
                              )}
                              emptyState={
                                <View
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                  }}
                                >
                                  <Text fontSize={2} color={'neutral.emphasis'}>
                                    {'해설을 스크랩 해주세요'}
                                  </Text>
                                </View>
                              }
                            />
                          </View>
                        </SquareView>
                      </View>
                    </View>
                  </Grid.Unit>
                </Grid>
              </>
            );
          }}
        </MutationFormik>
      </Suspense>
    </Dialog>
  );
};

export default BookBookScrapCreateDialog;
